import { BasePick, Sport } from '../domain/types';

type EspnTeam = { id?: string; abbreviation?: string; name?: string; displayName?: string; shortDisplayName?: string; location?: string; logos?: { href?: string }[] };
type TeamRow = { team?: EspnTeam };
type RosterItem = { id?: string; fullName?: string; displayName?: string; headshot?: { href?: string } | string; items?: RosterItem[] };
type SearchAthlete = { id?: string; uid?: string; displayName?: string; subtitle?: string; image?: { default?: string } };
export type PlayerImages = { photo?: string; teamLogo?: string; athleteId?: string };
const ESPN = 'https://site.api.espn.com/apis/site/v2/sports';
const tidy = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim().replace(/\s+(jr|sr|ii|iii|iv|v)$/i, '');
const sportPath = (sport: Sport) => sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
// Bump the cache so image URLs are re-resolved through the backend proxy.
const cacheKey = (sport: Sport, player: string) => `rushprops.playerImages.v8.${sport}.${tidy(player)}`;
const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
const memory = new Map<string, PlayerImages>();
const teamsCache = new Map<Sport, Promise<TeamRow[]>>();
const rostersCache = new Map<string, Promise<RosterItem[]>>();
const athleteSearchCache = new Map<string, Promise<SearchAthlete[]>>();

async function teams(sport: Sport): Promise<TeamRow[]> {
  let pending = teamsCache.get(sport);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams?limit=100`)
      .then(r => { if (!r.ok) throw new Error(`ESPN teams: ${r.status}`); return r.json(); })
      .then(data => data.sports?.[0]?.leagues?.[0]?.teams ?? [])
      .catch(error => { teamsCache.delete(sport); throw error; });
    teamsCache.set(sport, pending);
  }
  return pending;
}
async function roster(sport: Sport, id: string): Promise<RosterItem[]> {
  const key = `${sport}:${id}`;
  let pending = rostersCache.get(key);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams/${id}/roster`)
      .then(r => { if (!r.ok) throw new Error(`ESPN roster: ${r.status}`); return r.json(); })
      .then(data => (data.athletes ?? []).flatMap((row: RosterItem) => row.items ?? [row]))
      .catch(error => { rostersCache.delete(key); throw error; });
    rostersCache.set(key, pending);
  }
  return pending;
}
async function searchAthlete(sport: Sport, player: string, game: string, teamsForSport: TeamRow[]): Promise<SearchAthlete | undefined> {
  const key = `${sport}:${tidy(player)}`;
  let pending = athleteSearchCache.get(key);
  if (!pending) {
    const league = sport === Sport.NBA ? 'nba' : 'nhl';
    const query = new URLSearchParams({ query: player, limit: '5', sport: sport === Sport.NBA ? 'basketball' : 'hockey', league });
    pending = fetch(`https://site.api.espn.com/apis/search/v2?${query}`)
      .then(response => { if (!response.ok) throw new Error(`ESPN player search: ${response.status}`); return response.json(); })
      .then(data => {
        const players = (data.results ?? []).find((row: { type?: string }) => row.type === 'player')?.contents ?? [];
        return players.filter((row: SearchAthlete) => tidy(row.displayName ?? '') === tidy(player));
      })
      .catch(error => { athleteSearchCache.delete(key); throw error; });
    athleteSearchCache.set(key, pending);
  }
  const matches = await pending;
  if (matches.length < 2) return matches[0];
  const gameTeams = matchupTeams(game, teamsForSport);
  return matches.find(candidate => candidate.image?.default && gameTeams.some(team => aliases(team).some(alias => {
    const name = tidy(candidate.subtitle ?? '');
    return name === alias || name.endsWith(` ${alias}`) || alias.endsWith(` ${name}`);
  }))) ?? matches.find(candidate => candidate.image?.default) ?? matches[0];
}
function aliases(team: EspnTeam) {
  return [team.abbreviation, team.shortDisplayName, team.name, team.displayName, team.location]
    .filter((value): value is string => Boolean(value)).map(tidy).filter(Boolean);
}
function matchupTeams(game: string, rows: TeamRow[]): EspnTeam[] {
  // Odds feeds can format the same matchup as "Away @ Home" or "Away vs Home".
  const sides = game.split(/\s*(?:@|vs\.?|versus|\bat\b)\s*/i).map(tidy).filter(Boolean);
  if (sides.length !== 2) return [];
  return sides.map(side => rows.map(row => row.team).filter((team): team is EspnTeam => Boolean(team?.id && team.logos?.[0]?.href))
    .find(team => aliases(team).some(alias => alias === side || side.includes(` ${alias}`) || side.endsWith(` ${alias}`) || alias.includes(` ${side}`) || alias.startsWith(`${side} `)))
  ).filter((team): team is EspnTeam => Boolean(team));
}

export async function loadPlayerPhotos(picks: BasePick[]): Promise<Map<string, PlayerImages>> {
  const result = new Map<string, PlayerImages>();
  const unique = new Map<string, BasePick>();
  for (const pick of picks) {
    const key = cacheKey(pick.sport, pick.player);
    const stored = memory.get(key) ?? (() => { try { return JSON.parse(localStorage.getItem(key) ?? 'null') as PlayerImages | null; } catch { return null; } })();
    if (stored?.teamLogo || stored?.photo) {
      memory.set(key, stored);
      result.set(pick.id, stored);
    } else unique.set(key, pick);
  }
  await Promise.all(Array.from(unique.entries()).map(async ([key, pick]) => {
    try {
      const query = new URLSearchParams({ sport: pick.sport, player: pick.player });
      const response = await fetch(`${API_BASE}/player-image?${query}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const images = await response.json() as PlayerImages;
      if (!images.teamLogo && !images.photo) return;
      memory.set(key, images);
      try { localStorage.setItem(key, JSON.stringify(images)); } catch { /* Keep images in memory for this session. */ }
      for (const samePlayer of picks.filter(item => item.sport === pick.sport && tidy(item.player) === tidy(pick.player))) result.set(samePlayer.id, images);
    } catch { /* Keep the sport fallback when the image service is unavailable. */ }
  }));
  return result;
}

export type PlayerGameContext = { date: string; venue?: 'home' | 'away'; opponent?: string };
export type PlayerGameContextResult = { games: PlayerGameContext[]; currentOpponent?: string };
const gameContextCache = new Map<string, { expiresAt: number; rows: PlayerGameContext[] }>();
const seasonForNow = () => { const now = new Date(); return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear(); };
const contextKey = (sport: Sport, athleteId: string) => `rushprops.gamelog-context.v1.${sport}.${athleteId}.${seasonForNow()}`;

/** Resolve splits against the same real game history used by the prop's line. */
export async function loadPlayerGameContext(pick: BasePick): Promise<PlayerGameContextResult | null> {
  try {
    const rows = await teams(pick.sport);
    const gameTeams = matchupTeams(pick.game, rows);
    if (!gameTeams.length) return null;
    const rosters = await Promise.all(gameTeams.map(async team => ({
      team,
      players: await roster(pick.sport, team.id!).catch(() => []),
    })));
    const exactMatches = rosters.flatMap(({ team, players }) => players
      .filter(player => tidy(player.fullName ?? player.displayName ?? '') === tidy(pick.player))
      .map(player => ({ team, player })));
    const playerMatches = exactMatches.length ? exactMatches : rosters.flatMap(({ team, players }) => players
      .filter(player => {
        const actual = tidy(player.fullName ?? player.displayName ?? '').split(' ');
        const expected = tidy(pick.player).split(' ');
        return actual[actual.length - 1] === expected[expected.length - 1] && actual[0]?.[0] === expected[0]?.[0];
      })
      .map(player => ({ team, player })));
    if (playerMatches.length !== 1 || !playerMatches[0].player.id) return null;
    const { team, player } = playerMatches[0];
    const opponent = gameTeams.find(candidate => candidate.id !== team.id)?.abbreviation?.toUpperCase();
    const athleteId = String(player.id);
    const key = contextKey(pick.sport, athleteId);
    const cached = gameContextCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return { games: cached.rows, currentOpponent: opponent };
    try {
      const persisted = JSON.parse(localStorage.getItem(key) ?? 'null') as { expiresAt?: number; rows?: PlayerGameContext[] } | null;
      if (persisted?.expiresAt && persisted.expiresAt > Date.now() && Array.isArray(persisted.rows)) {
        gameContextCache.set(key, { expiresAt: persisted.expiresAt, rows: persisted.rows });
        return { games: persisted.rows, currentOpponent: opponent };
      }
    } catch { /* Use the live ESPN response when local storage is unavailable. */ }

    const sportPath = pick.sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
    const response = await fetch(`https://site.web.api.espn.com/apis/common/v3/sports/${sportPath}/athletes/${athleteId}/gamelog?season=${seasonForNow()}`);
    if (!response.ok) return { games: [], currentOpponent: opponent };
    const data = await response.json();
    const events = Object.values(data.events ?? {}) as Array<{ gameDate?: string; atVs?: string; homeAway?: string; opponent?: { abbreviation?: string } }>;
    const result = events.flatMap(event => {
      if (!event.gameDate) return [];
      const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(event.gameDate));
      const marker = String(event.atVs ?? event.homeAway ?? '').trim().toLowerCase();
      const venue: PlayerGameContext['venue'] = marker === 'vs' || marker === 'home' || marker.startsWith('vs ') ? 'home'
        : marker === '@' || marker === 'at' || marker === 'away' ? 'away' : undefined;
      return [{ date, venue, opponent: event.opponent?.abbreviation?.toUpperCase() }];
    });
    const expiresAt = Date.now() + 6 * 60 * 60 * 1000;
    gameContextCache.set(key, { expiresAt, rows: result });
    try { localStorage.setItem(key, JSON.stringify({ expiresAt, rows: result })); } catch { /* Session cache remains available. */ }
    return { games: result, currentOpponent: opponent };
  } catch { return null; }
}
